import { useState } from 'react';
import type { RotaPreview } from '../api/client.js';
import type { ControllerSession } from '../controller.js';
import { ImportPanel } from './SetupView.js';

/** Admin: turn the sign-ups into a rotation, check it, then use it. The CSV upload stays as a fallback. */
export function RotaPanel({ session }: { session: ControllerSession }) {
  const eventId = session.engine?.record.eventId;
  const rec = session.engine?.record ?? null;
  const [preview, setPreview] = useState<RotaPreview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [used, setUsed] = useState(false);
  if (!eventId) return null;

  const build = async () => {
    setErr(null);
    setUsed(false);
    try {
      setPreview(await session.client.generateRota(eventId));
    } catch (e) {
      setPreview(null);
      setErr((e instanceof Error ? e.message : String(e)).replace(/^[A-Z]+ \S+ -> \d+: /, '').replace(/^\{"error":"(.*)"\}$/, '$1'));
    }
  };
  const outs = preview ? Object.values(preview.summary.sitOuts) : [];
  const spread = outs.length ? Math.max(...outs) - Math.min(...outs) : 0;

  return (
    <div>
      <h2>Build the rotation from who signed up</h2>
      <p className="muted">
        Students are spread across the rooms so each meets a different room every round, and speakers stay in theirs. Build it as late as you can, then check it and use it. You can build it again if more people sign up.
      </p>
      <div className="row"><button className="primary" onClick={() => void build()}>Build rotation</button></div>
      {err && <div className="banner banner-error" role="alert">{err}</div>}
      {preview && (
        <div>
          <p>
            <strong>{preview.counts.students}</strong> students and <strong>{preview.counts.speakers}</strong> approved speakers
            {preview.counts.pendingSpeakers > 0 ? `; ${preview.counts.pendingSpeakers} speaker(s) still waiting for approval are not included` : ''}.
          </p>
          {preview.summary.warnings.length > 0 && (
            <div className="banner banner-warn"><ul>{preview.summary.warnings.map((w) => <li key={w}>{w}</li>)}</ul></div>
          )}
          {outs.some((n) => n > 0) && <p className="muted">Some students wait in the main session for a round when rooms are full (at most {Math.max(...outs)} each; the difference between students is {spread}).</p>}
          <div className="row">
            <button className="primary" disabled={!session.hasControl || used} onClick={() => void session.import(preview.files).then(() => setUsed(true))}>
              Use this rotation
            </button>
            {used && <span role="status">Imported. Check the findings below.</span>}
          </div>
        </div>
      )}
      {rec && rec.scheduleVersion && <p className="muted">Current rota: version {rec.scheduleVersion.version} ({rec.scheduleVersion.hash}), {rec.scheduleVersion.schedule.participants.length} people.</p>}

      <h2>Or upload CSV files</h2>
      <ImportPanel session={session} />
    </div>
  );
}
